import { Tag } from '@vrplatform/components';
import { useTeam } from '@vrplatform/data';
import {
  CircleDollarIcon,
  HomeIcon,
  LoaderIcon,
  PinIcon,
} from '@vrplatform/icons';
import { type Maybe, hasValue } from '@vrplatform/utils';
import type { listingOwnershipPeriodBusinessModel_enum } from '@vrplatform/graphql';
import { formatOwnershipBusinessModel } from '@vrplatform/ui-common';
import { type ComponentProps, useMemo } from 'react';
import { DrawerInfoCard } from '../../../drawers/_components';
import { ListingCollectionSelect } from './ListingCollectionSelect';
import { ListingStatusSelect } from './ListingStatusSelect';

type StatusSelectProps = ComponentProps<typeof ListingStatusSelect>;

type Props = {
  id: string;
  formattedAddress: string | undefined;
  collection: ComponentProps<typeof ListingCollectionSelect>['collection'];
  currentBusinessModel: Maybe<listingOwnershipPeriodBusinessModel_enum>;
} & StatusSelectProps;

export const ListingInfoCard = (listing: Props) => {
  const [{ isGL: GL }] = useTeam();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const rows = useMemo(
    () =>
      (
        [
          {
            icon: PinIcon,
            title: 'Address',
            text: listing.formattedAddress,
          },
          !GL || !listing.currentBusinessModel
            ? undefined
            : {
                icon: CircleDollarIcon,
                title: 'Business model',
                text: (
                  <Tag>
                    {formatOwnershipBusinessModel(listing.currentBusinessModel)}
                  </Tag>
                ),
              },
          GL
            ? undefined
            : {
                icon: LoaderIcon,
                title: 'Automations',
                text: <ListingStatusSelect inputDisabled {...listing} />,
              },
          GL
            ? undefined
            : {
                icon: HomeIcon,
                title: 'Listing Group',
                text: (
                  <ListingCollectionSelect
                    collection={listing.collection}
                    listingId={listing.id}
                  />
                ),
              },
        ] satisfies (
          | ComponentProps<typeof DrawerInfoCard>['rows'][number]
          | undefined
        )[]
      ).filter(hasValue),
    [listing.collection, listing.formattedAddress, listing.id, GL]
  );

  return <DrawerInfoCard rows={rows} labelWidth={150} />;
};
